import React, { useState, useEffect, useRef } from 'react'
import { getLogs } from '../services/api'
import { parseScoreLegacy, formatContextLegacy, resolvePassedSafe, extractDetails } from '../services/legacyParsers'
import { formatError } from '../utils/errorHandler'
import * as XLSX from 'xlsx'
import './Dashboard.css'

const EVENT_TYPES = [
  'Lesson Section Completed',
  'Lesson Started',
  'Lesson Start',
  'Scenario Switch',
  'Lesson Completed',
  'Lesson Completion',
  'Lesson Result',
  'Session Start',
  'Persona Change',
  'Scenario Change',
  'Feedback Request',
  'Lesson End'
]

function Dashboard() {
  const [logs, setLogs] = useState([])
  const [filteredLogs, setFilteredLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)
  const [selectedEventTypes, setSelectedEventTypes] = useState([])
  const [showEventDropdown, setShowEventDropdown] = useState(false)
  const [eventSearch, setEventSearch] = useState('')
  const [selectedUsers, setSelectedUsers] = useState([])
  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const eventDropdownRef = useRef(null)
  const userDropdownRef = useRef(null)
  const [filters, setFilters] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    limit: 100
  })
  
  // Column selector state
  const [showColumnSelector, setShowColumnSelector] = useState(false)
  const columnSelectorRef = useRef(null)
  
  // Toast notification state
  const [toast, setToast] = useState(null)
  const toastTimeoutRef = useRef(null)
  
  // Define all available columns
  const allColumns = [
    { key: 'Timestamp', label: 'Timestamp' },
    { key: 'Event Type', label: 'Event Type' },
    { key: 'User', label: 'User' },
    { key: 'Summary', label: 'Summary' },
    { key: 'Lesson Name / Module Name', label: 'Lesson Name / Module Name' },
    { key: 'In Progress / Completed', label: 'In Progress / Completed' },
    { key: 'Completed Status', label: 'Completed Status' },
    { key: 'Points Earned', label: 'Points Earned' },
    { key: 'Total Points', label: 'Total Points' },
    { key: 'Score %', label: 'Score %' },
    { key: 'Quiz Score', label: 'Quiz Score' },
    { key: 'Pitch Score', label: 'Pitch Score' },
    { key: 'Pitch Score %', label: 'Pitch Score %' },
    { key: 'Pitch Count', label: 'Pitch Count' },
    { key: 'Gauntlet Score', label: 'Gauntlet Score' },
    { key: 'Gauntlet Score %', label: 'Gauntlet Score %' },
    { key: 'Gauntlet Count', label: 'Gauntlet Count' },
    { key: 'Context', label: 'Context' },
    { key: 'Log ID', label: 'Log ID' }
  ]
  
  // Default preselected columns (based on requirements - most commonly used)
  const defaultSelectedColumns = [
    'Timestamp',
    'Event Type',
    'User',
    'Summary',
    'Lesson Name / Module Name',
    'In Progress / Completed',
    'Completed Status',
    'Points Earned',
    'Total Points',
    'Score %',
    'Quiz Score',
    'Pitch Score',
    'Pitch Score %',
    'Gauntlet Score',
    'Gauntlet Score %'
  ]
  
  const [selectedColumns, setSelectedColumns] = useState(defaultSelectedColumns)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(event.target)) {
        setShowEventDropdown(false)
        setEventSearch('')
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false)
        setUserSearch('')
      }
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setShowColumnSelector(false)
      }
    }

    if (showEventDropdown || showUserDropdown || showColumnSelector) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showEventDropdown, showUserDropdown, showColumnSelector])

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    loadLogs()
  }, [filters.limit])

  useEffect(() => {
    applyFilters()
  }, [logs, filters, selectedEventTypes, selectedUsers])

  const uniqueUsers = React.useMemo(() => {
    const users = new Set()
    logs.forEach(log => {
      if (log.user_id) users.add(log.user_id)
    })
    return [...users].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
  }, [logs])

  const loadLogs = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getLogs(filters.limit, 90)
      const logs = data.logs || []
      logs.sort((a, b) => {
        const dateA = new Date(a.timestamp)
        const dateB = new Date(b.timestamp)
        return dateB - dateA
      })
      setLogs(logs)
      setLastUpdated(new Date())
    } catch (err) {
      setError(formatError(err))
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...logs]

    if (selectedEventTypes.length > 0) {
      filtered = filtered.filter(log => 
        selectedEventTypes.includes(log.event_type)
      )
    }

    if (selectedUsers.length > 0) {
      filtered = filtered.filter(log => 
        selectedUsers.includes(log.user_id)
      )
    }

    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(log => {
        const summaryMatch = log.summary?.toLowerCase().includes(searchLower)
        const contextMatch = log.context
          ? JSON.stringify(log.context).toLowerCase().includes(searchLower)
          : false
        return summaryMatch || contextMatch
      })
    }

    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom)
      fromDate.setHours(0, 0, 0, 0)
      filtered = filtered.filter(log => new Date(log.timestamp) >= fromDate)
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo)
      toDate.setHours(23, 59, 59, 999)
      filtered = filtered.filter(log => new Date(log.timestamp) <= toDate)
    }

    filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

    setFilteredLogs(filtered)
  }

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value })
  }

  const handleEventTypeToggle = (eventType) => {
    setSelectedEventTypes(prev => {
      if (prev.includes(eventType)) {
        return prev.filter(type => type !== eventType)
      } else {
        return [...prev, eventType]
      }
    })
  }

  const handleSelectAllEvents = () => {
    if (selectedEventTypes.length === EVENT_TYPES.length) {
      setSelectedEventTypes([])
    } else {
      setSelectedEventTypes([...EVENT_TYPES])
    }
  }

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(u => u !== userId)
      } else {
        return [...prev, userId]
      }
    })
  }

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === uniqueUsers.length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers([...uniqueUsers])
    }
  }

  const filteredEventList = React.useMemo(() => {
    if (!eventSearch.trim()) return EVENT_TYPES
    const searchLower = eventSearch.toLowerCase()
    return EVENT_TYPES.filter(t => t.toLowerCase().includes(searchLower))
  }, [eventSearch])

  const filteredUserList = React.useMemo(() => {
    if (!userSearch.trim()) return uniqueUsers
    const searchLower = userSearch.toLowerCase()
    return uniqueUsers.filter(u => u.toLowerCase().includes(searchLower))
  }, [uniqueUsers, userSearch])

  const clearDateFilters = () => {
    setFilters({ ...filters, dateFrom: '', dateTo: '' })
  }

  // Helper function to extract score from various formats
  const extractScore = (scoreValue) => {
    if (!scoreValue) return { earned: null, total: null, percentage: null }
    
    const scoreStr = String(scoreValue)
    // Try to match "8/10" or "16/20" format
    const match = scoreStr.match(/(\d+)\/(\d+)/)
    if (match) {
      const earned = parseInt(match[1])
      const total = parseInt(match[2])
      const percentage = total > 0 ? Math.round((earned / total) * 100) : null
      return { earned, total, percentage }
    }
    
    // If it's just a number, assume it's a percentage
    const num = parseFloat(scoreStr)
    if (!isNaN(num)) {
      return { earned: null, total: null, percentage: num }
    }
    
    return { earned: null, total: null, percentage: null }
  }

  // Build completion status map: user+module -> { hasQuiz, hasPitch, hasGauntlet }
  const buildCompletionMap = (logs) => {
    const completionMap = new Map()
    
    logs.forEach(log => {
      if (!log.context?.module_name || !log.user_id) return
      
      const key = `${log.user_id}|||${log.context.module_name}`
      if (!completionMap.has(key)) {
        completionMap.set(key, { hasQuiz: false, hasPitch: false, hasGauntlet: false })
      }
      
      const status = completionMap.get(key)
      const context = log.context
      const sectionData = context.section_data
      
      if (context.quiz_score || sectionData?.quiz_score || context.score || context.section_type === 'quiz') {
        status.hasQuiz = true
      }
      if (context.pitch_score || sectionData?.pitch_score || (context.section_type === 'pitch' && context.score)) {
        status.hasPitch = true
      }
      if (context.gauntlet_completed !== undefined || sectionData?.battles_completed ||
          context.encounters_completed || context.section_type === 'gauntlet') {
        status.hasGauntlet = true
      }
    })
    
    return completionMap
  }

  // Build counts map: user+module -> { pitchCount, gauntletCount }
  const buildCountsMap = (logs) => {
    const countsMap = new Map()
    
    logs.forEach(log => {
      if (!log.context?.module_name || !log.user_id) return
      
      const key = `${log.user_id}|||${log.context.module_name}`
      if (!countsMap.has(key)) {
        countsMap.set(key, { pitchCount: 0, gauntletCount: 0 })
      }
      
      const counts = countsMap.get(key)
      const context = log.context
      const sectionData = context.section_data
      
      if (context.section_type === 'pitch' && (context.score || context.pitch_score || sectionData?.pitch_score)) {
        counts.pitchCount++
      }
      if (context.section_type === 'gauntlet' && (context.encounters_completed || context.gauntlet_completed !== undefined || sectionData?.battles_completed)) {
        counts.gauntletCount++
      }
    })
    
    return countsMap
  }

  // Get completion status for a log entry
  const getCompletionStatus = (log, completionMap) => {
    if (!log.context?.module_name || !log.user_id) return ''
    
    const key = `${log.user_id}|||${log.context.module_name}`
    const status = completionMap.get(key)
    
    if (!status) return ''
    
    // If all three are required and all are present, mark as Completed
    if (status.hasQuiz && status.hasPitch && status.hasGauntlet) {
      return 'Completed'
    }
    
    // Otherwise, check event type for additional context
    if (log.event_type === 'Lesson Completed' || log.event_type === 'Lesson End') {
      return 'Completed'
    }
    
    return 'In Progress'
  }

  // Format timestamp to date only (MM/DD/YYYY)
  const formatDateOnly = (timestamp) => {
    if (!timestamp) return ''
    try {
      const date = new Date(timestamp)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()
      return `${month}/${day}/${year}`
    } catch {
      return timestamp
    }
  }

  // Get "In Progress / Completed" status based on event type
  const getProgressStatus = (log) => {
    const inProgressEvents = ['Lesson Started', 'Lesson Start', 'Lesson Section Completed']
    const completedEvents = ['Lesson Completed', 'Lesson End']
    
    if (inProgressEvents.includes(log.event_type)) {
      return 'In Progress'
    }
    if (completedEvents.includes(log.event_type)) {
      return 'Completed'
    }
    return ''
  }

  // Handle column selection toggle
  const handleColumnToggle = (columnKey) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnKey)) {
        return prev.filter(key => key !== columnKey)
      } else {
        return [...prev, columnKey]
      }
    })
  }

  // Handle select all columns
  const handleSelectAllColumns = () => {
    if (selectedColumns.length === allColumns.length) {
      setSelectedColumns([])
    } else {
      setSelectedColumns(allColumns.map(col => col.key))
    }
  }

  // Handle reset to defaults
  const handleResetToDefaults = () => {
    setSelectedColumns(defaultSelectedColumns)
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    // Clear existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current)
    }
    
    setToast({ message, type })
    
    // Auto-dismiss after 4 seconds
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null)
    }, 4000)
  }

  const exportToExcel = () => {
    if (filteredLogs.length === 0) {
      showToast('No data to export. Please refresh and apply filters first.', 'error')
      return
    }

    if (selectedColumns.length === 0) {
      showToast('Please select at least one column to export.', 'error')
      return
    }

    try {
      // Build completion and counts maps
      const completionMap = buildCompletionMap(filteredLogs)
      const countsMap = buildCountsMap(filteredLogs)

      // Prepare data for export
      const exportData = filteredLogs.map(log => {
        const context = log.context || {}
        const sectionData = context.section_data || {}
        const moduleName = context.module_name || context.current_lesson || context.lesson_type || ''
        const userModuleKey = log.user_id && moduleName ? `${log.user_id}|||${moduleName}` : null
        
        // Get completion status
        const completionStatus = getCompletionStatus(log, completionMap)
        const progressStatus = getProgressStatus(log)
        
        // Get counts
        const counts = userModuleKey ? countsMap.get(userModuleKey) : null
        const pitchCount = counts?.pitchCount || 0
        const gauntletCount = counts?.gauntletCount || 0
        
        const quizScore = context.quiz_score || sectionData.quiz_score
          || (context.section_type === 'quiz' ? context.score : '')
          || ''

        const pitchScore = context.pitch_score || sectionData.pitch_score
          || (context.section_type === 'pitch' ? context.score : '')
          || ''
        const pitchScoreData = extractScore(pitchScore)
        const pitchScorePercent = pitchScoreData.percentage !== null ? `${pitchScoreData.percentage}%` : ''

        const gauntletCompleted = context.gauntlet_completed !== undefined
          ? context.gauntlet_completed
          : (sectionData.battles_completed !== undefined
            ? sectionData.battles_completed
            : (context.encounters_completed !== undefined
              ? context.encounters_completed
              : ''))
        const gauntletScore = gauntletCompleted !== '' ? String(gauntletCompleted) : ''

        let gauntletScorePercent = ''
        const bWon = sectionData.battles_won ?? context.encounters_won
        const bCompleted = sectionData.battles_completed ?? context.encounters_completed
        if (bWon !== undefined && bCompleted !== undefined && parseInt(bCompleted) > 0) {
          gauntletScorePercent = `${Math.round((parseInt(bWon) / parseInt(bCompleted)) * 100)}%`
        }
        
        // Extract general score data
        const score = parseScore(log)
        
        const fullRow = {
          'Timestamp': formatDateOnly(log.timestamp),
          'Event Type': log.event_type,
          'User': log.user_id,
          'Summary': log.summary || '',
          'Lesson Name / Module Name': moduleName,
          'In Progress / Completed': progressStatus,
          'Completed Status': completionStatus,
          'Points Earned': score.pointsEarned || '',
          'Total Points': score.totalPoints || '',
          'Score %': score.scorePercentage || '',
          'Quiz Score': quizScore,
          'Pitch Score': pitchScore,
          'Pitch Score %': pitchScorePercent,
          'Pitch Count': pitchCount,
          'Gauntlet Score': gauntletScore,
          'Gauntlet Score %': gauntletScorePercent,
          'Gauntlet Count': gauntletCount,
          'Context': log.context ? JSON.stringify(log.context) : '',
          'Log ID': log.log_id
        }
        
        // Only include selected columns
        const row = {}
        selectedColumns.forEach(colKey => {
          if (fullRow.hasOwnProperty(colKey)) {
            row[colKey] = fullRow[colKey]
          }
        })
        
        return row
      })

      // Create workbook
      const wb = XLSX.utils.book_new()
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData)
      
      // Set column widths
      const colWidths = [
        { wch: 12 }, // Timestamp
        { wch: 25 }, // Event Type
        { wch: 30 }, // User
        { wch: 50 }, // Summary
        { wch: 30 }, // Lesson Name / Module Name
        { wch: 20 }, // In Progress / Completed
        { wch: 18 }, // Completed Status
        { wch: 12 }, // Points Earned
        { wch: 12 }, // Total Points
        { wch: 10 }, // Score %
        { wch: 12 }, // Quiz Score
        { wch: 12 }, // Pitch Score
        { wch: 12 }, // Pitch Score %
        { wch: 12 }, // Pitch Count
        { wch: 15 }, // Gauntlet Score
        { wch: 15 }, // Gauntlet Score %
        { wch: 15 }, // Gauntlet Count
        { wch: 50 }, // Context
        { wch: 40 }  // Log ID
      ]
      ws['!cols'] = colWidths
      
      // Format header row (bold)
      const headerRange = XLSX.utils.decode_range(ws['!ref'])
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col })
        if (!ws[cellAddress]) continue
        ws[cellAddress].s = {
          font: { bold: true },
          alignment: { horizontal: 'center', vertical: 'center' }
        }
      }
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Ditto Logs')
      
      // Generate filename
      const now = new Date()
      const dateStr = now.toISOString().split('T')[0]
      const filename = `ditto-logs-${dateStr}.xlsx`
      
      // Download the file
      XLSX.writeFile(wb, filename)
      
      // Show success message
      showToast(`Exported ${filteredLogs.length} logs to Excel`, 'success')
      
    } catch (error) {
      console.error('Export error:', error)
      showToast('Failed to export data. Please try again.', 'error')
    }
  }

  const getPointsEarned = (log) => {
    return log.context?.points_earned || log.context?.pointsEarned || ''
  }

  const getTotalPoints = (log) => {
    return log.context?.total_points || log.context?.totalPoints || ''
  }

  const getScorePercent = (log) => {
    return log.context?.score_percent || log.context?.scorePercent || log.context?.score || ''
  }

  const formatContext = (context) => {
    if (!context) return ''
    try {
      return JSON.stringify(context, null, 2)
    } catch {
      return String(context)
    }
  }

  const parseScore = (log) => {
    let pointsEarned = ''
    let totalPoints = ''
    let scorePercentage = ''

    const parseFraction = (raw) => {
      const cleaned = String(raw).replace(/\s*\(.*\)/, '')
      const match = cleaned.match(/(\d+)\/(\d+)/)
      if (match) {
        pointsEarned = match[1]
        totalPoints = match[2]
        scorePercentage = Math.round((parseInt(match[1]) / parseInt(match[2])) * 100) + '%'
        return true
      }
      return false
    }
    
    if (log.context) {
      pointsEarned = log.context.points_earned || log.context.pointsEarned || ''
      totalPoints = log.context.total_points || log.context.totalPoints || ''
      scorePercentage = log.context.score_percentage || log.context.scorePercent || ''

      if (pointsEarned && totalPoints) {
        pointsEarned = String(pointsEarned)
        totalPoints = String(totalPoints)
        if (!scorePercentage) {
          const total = parseInt(totalPoints)
          if (total > 0) {
            scorePercentage = Math.round((parseInt(pointsEarned) / total) * 100) + '%'
          }
        }
        return { pointsEarned, totalPoints, scorePercentage }
      }

      if (!pointsEarned) {
        const scoreField = log.context.score || log.context.quiz_score || log.context.pitch_score
        if (scoreField) parseFraction(scoreField)
      }

      if (!pointsEarned && log.context.section_data) {
        const sd = log.context.section_data
        const sdScore = sd.pitch_score || sd.quiz_score
        if (sdScore) parseFraction(sdScore)
      }
      
      if (!pointsEarned && log.summary) {
        const match = log.summary.match(/Score:\s*(\d+)\/(\d+)/i) || log.summary.match(/(\d+)\/(\d+)/)
        if (match) parseFraction(match[0])
      }
    }

    if (!pointsEarned && log.context) {
      const ctx = log.context
      const sd = ctx.section_data || {}
      const sType = ctx.section_type
      if (sType === 'gauntlet' || sType === 'roleplay') {
        const won = ctx.encounters_won ?? sd.battles_won
        const completed = ctx.encounters_completed ?? sd.battles_completed
        if (won !== undefined && completed !== undefined) {
          pointsEarned = String(won)
          totalPoints = String(completed)
          const c = parseInt(completed)
          if (c > 0) {
            scorePercentage = Math.round((parseInt(won) / c) * 100) + '%'
          }
        }
      }
    }

    if (!pointsEarned && !scorePercentage) {
      try {
        const legacy = parseScoreLegacy(log)
        pointsEarned = legacy.pointsEarned
        totalPoints = legacy.totalPoints
        scorePercentage = legacy.scorePercentage
      } catch (e) { /* silent */ }
    }

    return { pointsEarned, totalPoints, scorePercentage }
  }

  const formatDetailContext = (log) => {
    if (!log || !log.context) return null
    
    const context = log.context
    const score = parseScore(log)
    
    // Check if it's a lesson section completion
    if (context.section_type) {
      const sectionType = context.section_type
      const sectionData = context.section_data || {}
      const merged = { ...sectionData, ...context }
      const moduleName = context.module_name || 'Unknown Module'
      
      let breakdown = []
      let scoreRaw = merged.score || sectionData.pitch_score || sectionData.quiz_score || merged.quiz_score || merged.pitch_score || ''
      let passed = resolvePassedSafe(merged, scoreRaw)

      if (sectionType === 'pitch') {
        const rubric = merged.rubric || sectionData.rubric_breakdown
        if (rubric) {
          if (typeof rubric === 'string') {
            const metrics = rubric.split(',').map(m => m.trim())
            breakdown = metrics.map(metric => {
              const parts = metric.split(':')
              if (parts.length === 2) {
                return { name: parts[0].trim(), score: parts[1].trim() }
              }
              return null
            }).filter(Boolean)
          } else if (typeof rubric === 'object' && rubric !== null) {
            breakdown = Object.entries(rubric).map(([key, value]) => ({
              name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              score: String(value)
            }))
          }
        }
        
        if (breakdown.length === 0) {
          const breakdownFields = ['accuracy', 'clarity', 'objection_handling', 'persuasion', 'engagement', 'comprehension']
          breakdownFields.forEach(field => {
            const val = merged[field] ?? sectionData[field]
            if (val !== undefined) {
              breakdown.push({
                name: field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                score: String(val)
              })
            }
          })
        }
      }

      if (sectionType === 'gauntlet' || sectionType === 'roleplay') {
        const battlesCompleted = merged.battles_completed ?? merged.encounters_completed ?? sectionData.completed_scenarios
        const battlesWon = merged.battles_won ?? merged.encounters_won
        const perfSummary = merged.performance_summary ?? merged.performance ?? sectionData.performance_summary

        if (battlesCompleted !== undefined) {
          scoreRaw = battlesWon !== undefined
            ? `${battlesWon}/${battlesCompleted} encounters won`
            : `${battlesCompleted} encounters`
        }

        if (perfSummary) {
          const entries = String(perfSummary).split(/[;,]/).map(e => e.trim()).filter(Boolean)
          breakdown = entries.map(entry => {
            const parts = entry.split(':')
            if (parts.length === 2) return { name: parts[0].trim(), score: parts[1].trim() }
            return { name: entry, score: '' }
          })
        }

        passed = null
      }
      
      const result = {
        title: moduleName,
        section: sectionType.toUpperCase(),
        score: scoreRaw,
        passed,
        breakdown
      }

      if (sectionType === 'objectives') {
        result.details = extractDetails(context)
      }

      return result
    }

    if (log.event_type === 'Lesson Started' || log.event_type === 'Lesson Start') {
      return {
        title: context.module_name || context.current_lesson || context.lesson_type || 'Lesson',
        section: 'LESSON STARTED',
        score: null,
        passed: null,
        breakdown: [],
        details: extractDetails(context)
      }
    }

    try {
      return formatContextLegacy(log)
    } catch (e) {
      return null
    }
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Logging Dashboard</h2>
        <p className="dashboard-description">
          Use the search bar to filter by summary or context. Click the <strong>Event Type</strong> or <strong>User</strong> column headers to filter by type or search for specific users. Only loaded data is searched.
        </p>
      </div>

      <div className="dashboard-filters">
        <div className="filter-row">
          <div className="filter-item search-item">
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search summary & context..."
              className="search-input"
            />
          </div>

          <div className="filter-item date-item">
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
            {(filters.dateFrom || filters.dateTo) && (
              <button className="clear-btn" onClick={clearDateFilters}>Clear</button>
            )}
          </div>

          <div className="filter-item compact-limit-item">
            <select
              value={filters.limit}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="compact-limit-select"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>

          <div className="filter-item action-buttons-item">
            <button className="refresh-btn" onClick={loadLogs} title="Refresh">
              Refresh
            </button>
            <button className="export-btn" onClick={() => setShowColumnSelector(true)} title="Export to Excel">
              Export
            </button>
          </div>
        </div>
        <div className="search-note">Searching loaded data only</div>
      </div>

      {loading && (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading logs...</p>
        </div>
      )}
      {error && <div className="error">Error: {error}</div>}

      {!loading && !error && (
        <>
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th className="th-filterable" ref={eventDropdownRef}>
                  <div className="th-filter-trigger" onClick={() => { setShowEventDropdown(!showEventDropdown); setShowUserDropdown(false) }}>
                    <span>Event Type</span>
                    <span className={`th-filter-icon ${selectedEventTypes.length > 0 ? 'active' : ''}`}>&#9662;</span>
                  </div>
                  {showEventDropdown && (
                    <div className="th-filter-dropdown">
                      <div className="th-filter-search">
                        <input
                          type="text"
                          value={eventSearch}
                          onChange={(e) => setEventSearch(e.target.value)}
                          placeholder="Search event types..."
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="th-filter-option">
                        <input
                          type="checkbox"
                          id="th_event_all"
                          checked={selectedEventTypes.length === EVENT_TYPES.length}
                          onChange={handleSelectAllEvents}
                        />
                        <label htmlFor="th_event_all">All Events</label>
                      </div>
                      <div className="th-filter-scrollable">
                        {filteredEventList.map(type => (
                          <div key={type} className="th-filter-option">
                            <input
                              type="checkbox"
                              id={`th_event_${type.replace(/\s+/g, '_').toLowerCase()}`}
                              checked={selectedEventTypes.includes(type)}
                              onChange={() => handleEventTypeToggle(type)}
                            />
                            <label htmlFor={`th_event_${type.replace(/\s+/g, '_').toLowerCase()}`}>{type}</label>
                          </div>
                        ))}
                        {filteredEventList.length === 0 && (
                          <div className="th-filter-empty">No event types match</div>
                        )}
                      </div>
                      {selectedEventTypes.length > 0 && (
                        <div className="th-filter-clear" onClick={() => setSelectedEventTypes([])}>
                          Clear filter
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th className="th-filterable" ref={userDropdownRef}>
                  <div className="th-filter-trigger" onClick={() => { setShowUserDropdown(!showUserDropdown); setShowEventDropdown(false) }}>
                    <span>User</span>
                    <span className={`th-filter-icon ${selectedUsers.length > 0 ? 'active' : ''}`}>&#9662;</span>
                  </div>
                  {showUserDropdown && (
                    <div className="th-filter-dropdown th-user-dropdown">
                      <div className="th-filter-search">
                        <input
                          type="text"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          placeholder="Search users..."
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="th-filter-option">
                        <input
                          type="checkbox"
                          id="th_user_all"
                          checked={selectedUsers.length === uniqueUsers.length && uniqueUsers.length > 0}
                          onChange={handleSelectAllUsers}
                        />
                        <label htmlFor="th_user_all">All Users</label>
                      </div>
                      <div className="th-filter-scrollable">
                        {filteredUserList.map(user => (
                          <div key={user} className="th-filter-option">
                            <input
                              type="checkbox"
                              id={`th_user_${user.replace(/[^a-zA-Z0-9]/g, '_')}`}
                              checked={selectedUsers.includes(user)}
                              onChange={() => handleUserToggle(user)}
                            />
                            <label htmlFor={`th_user_${user.replace(/[^a-zA-Z0-9]/g, '_')}`}>{user}</label>
                          </div>
                        ))}
                        {filteredUserList.length === 0 && (
                          <div className="th-filter-empty">No users match</div>
                        )}
                      </div>
                      {selectedUsers.length > 0 && (
                        <div className="th-filter-clear" onClick={() => setSelectedUsers([])}>
                          Clear filter
                        </div>
                      )}
                    </div>
                  )}
                </th>
                <th>Summary</th>
                <th>Points Earned</th>
                <th>Total Points</th>
                <th>Score %</th>
                <th>Context</th>
                <th>Log ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data-cell">
                    No logs found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log, index) => {
                  const score = parseScore(log)
                  const detailContext = formatDetailContext(log)
                  return (
                    <tr key={log.log_id || index}>
                      <td className="timestamp">{new Date(log.timestamp).toLocaleString()}</td>
                      <td>
                        <span className="event-badge">{log.event_type}</span>
                      </td>
                      <td className="user-id">{log.user_id}</td>
                      <td className="summary">{log.summary || ''}</td>
                      <td className="points score-earned">{score.pointsEarned || ''}</td>
                      <td className="points score-total">{score.totalPoints || ''}</td>
                      <td className="score">{score.scorePercentage || ''}</td>
                      <td className="context">
                        {detailContext ? (
                          <div className="lesson-context">
                            <div className="lesson-module">
                              <strong>{detailContext.title}</strong>
                            </div>
                            <div className="lesson-section">
                              <strong>Section:</strong> {detailContext.section}
                            </div>
                            {detailContext.score && (
                              <div className="performance-metric">
                                <strong>
                                  {detailContext.section === 'PITCH' ? 'Pitch' : detailContext.section === 'QUIZ' ? 'Quiz' : 'Score'}:
                                </strong> {detailContext.score}
                                {detailContext.passed === true && ' PASSED'}
                                {detailContext.passed === false && ' FAILED'}
                              </div>
                            )}
                            {detailContext.breakdown && detailContext.breakdown.length > 0 && (
                              <div className="rubric-detail">
                                {detailContext.breakdown.map((item, idx) => (
                                  <span key={idx}>
                                    {item.name}: {item.score}{idx < detailContext.breakdown.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            )}
                            {detailContext.details && (
                              <div className="context-details">
                                {detailContext.details.row1.length > 0 && (
                                  <div className="detail-row">
                                    {detailContext.details.row1.map((item, idx) => (
                                      <span key={idx} className="detail-tag">
                                        <strong>{item.label}:</strong> {item.value}
                                        {idx < detailContext.details.row1.length - 1 ? ' | ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {detailContext.details.row2.length > 0 && (
                                  <div className="detail-row">
                                    {detailContext.details.row2.map((item, idx) => (
                                      <span key={idx} className="detail-tag">
                                        <strong>{item.label}:</strong> {item.value}
                                        {idx < detailContext.details.row2.length - 1 ? ' | ' : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : log.context ? (
                          <div className="context-item">
                            {Object.entries(log.context).map(([key, value]) => (
                              <div key={key} className="context-item-row">
                                <strong>{key}:</strong> {typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value)}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <em>No context</em>
                        )}
                      </td>
                      <td><code className="log-id">{log.log_id}</code></td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="table-status-bar">
          <div className="status-bar-left">
            <span className={`status-indicator ${!loading && !error ? 'connected' : ''}`}></span>
            <span className="status-text">
              {loading ? 'Loading...' : error ? 'Error' : 'Connected'}
            </span>
            <span className="logs-count">
              {filteredLogs.length} {filteredLogs.length === 1 ? 'log' : 'logs'} loaded
            </span>
          </div>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
        </>
      )}

      {/* Column Selector Modal */}
      {showColumnSelector && (
        <div className="modal-overlay" onClick={() => setShowColumnSelector(false)}>
          <div className="modal-content column-selector-modal" ref={columnSelectorRef} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Columns for Export</h3>
              <button className="modal-close" onClick={() => setShowColumnSelector(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="column-selector-actions">
                <button className="select-all-btn" onClick={handleSelectAllColumns}>
                  {selectedColumns.length === allColumns.length ? 'Deselect All' : 'Select All'}
                </button>
                <button className="reset-defaults-btn" onClick={handleResetToDefaults}>
                  Reset to Defaults
                </button>
                <span className="selected-count">
                  {selectedColumns.length} of {allColumns.length} selected
                </span>
              </div>
              <div className="column-selector-list">
                {allColumns.map(column => (
                  <div key={column.key} className="column-selector-item">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(column.key)}
                        onChange={() => handleColumnToggle(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowColumnSelector(false)}>
                Cancel
              </button>
              <button className="export-confirm-btn" onClick={() => {
                setShowColumnSelector(false)
                exportToExcel()
              }}>
                Export with Selected Columns
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-icon">
              {toast.type === 'success' ? '✓' : '✗'}
            </span>
            <span className="toast-message">{toast.message}</span>
          </div>
          <button 
            className="toast-close" 
            onClick={() => setToast(null)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      )}
    </div>
  )
}

export default Dashboard
